import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Calendar, Clock, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function TimeSlotSelector({ duel, playground, onSuccess, onError }) {
  const queryClient = useQueryClient();
  const [selectedSlotIndex, setSelectedSlotIndex] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);

  // Calculate time remaining
  useEffect(() => {
    if (!duel?.slot_selection_deadline_at) return;

    const updateTimer = () => {
      const now = new Date();
      const raw = String(duel.slot_selection_deadline_at);
      const deadline = new Date(raw.endsWith('Z') || raw.includes('+') ? raw : raw + 'Z');
      const diff = deadline - now;
      setTimeRemaining(Math.max(0, diff));
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [duel?.slot_selection_deadline_at]);

  // Format time remaining
  const formatTimeRemaining = (milliseconds) => {
    const totalSec = Math.floor(milliseconds / 1000);
    if (totalSec <= 0) return '0s';
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}min`;
    if (m >= 10) return `${m}min ${s.toString().padStart(2, '0')}s`;
    if (m > 0) return `${m}:${s.toString().padStart(2, '0')}`;
    return `${s}s`;
  };

  // Select time slot mutation
  const selectSlotMutation = useMutation({
    mutationFn: async () => {
      if (selectedSlotIndex === null) throw new Error('Veuillez sélectionner un créneau');

      const selectedSlot = duel.proposed_time_slots[selectedSlotIndex];
      const { error } = await supabase.from('court_duels').update({
        status: 'SCHEDULED',
        scheduled_at: selectedSlot,
        slot_selected_at: new Date().toISOString()
      }).eq('id', duel.id);
      if (error) throw error;
      return { success: true };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['duel', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
      if (onSuccess) {
        onSuccess({
          type: 'success',
          title: 'Créneau confirmé',
          message: data.message || 'Le match est programmé !'
        });
      }
    },
    onError: (error) => {
      if (onError) {
        onError({
          type: 'error',
          title: 'Erreur',
          message: error.response?.data?.error || error.message || 'Échec de la sélection du créneau'
        });
      }
    }
  });

  if (!duel?.proposed_time_slots || duel.proposed_time_slots.length === 0) {
    return (
      <Card className="bg-gradient-to-br from-red-900/30 to-red-800/20 border-2 border-red-600 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-400 uppercase">
              Erreur de configuration
            </p>
            <p className="text-xs text-[#B3B6C2] mt-1">
              Le champ "proposed_time_slots" n'existe pas dans la base de données. Veuillez ajouter ce champ au schéma de l'entité CourtDuel (type: JSON Array, nullable).
            </p>
            <p className="text-xs text-amber-400 mt-2 font-mono">
              Champs requis: proposed_time_slots, slot_selection_deadline_at, slots_proposed_at
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const isExpired = timeRemaining === 0;

  return (
    <div className="space-y-3">
      {/* Header with countdown */}
      <Card className="bg-gradient-to-br from-amber-900/30 to-amber-800/20 border-2 border-amber-600 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-400 uppercase">
              Créneaux proposés par le défenseur
            </p>
            <p className="text-xs text-[#B3B6C2] mt-1">
              {isExpired
                ? 'Délai expiré - Le duel est maintenant ouvert à tous'
                : `Sélectionnez un créneau - Temps restant: ${formatTimeRemaining(timeRemaining)}`
              }
            </p>
          </div>
        </div>
      </Card>

      {/* Time slots list */}
      {!isExpired && (
        <>
          <div className="space-y-2">
            {duel.proposed_time_slots.map((slot, index) => {
              const slotDate = new Date(slot);
              const isSelected = selectedSlotIndex === index;

              return (
                <Card
                  key={index}
                  onClick={() => setSelectedSlotIndex(index)}
                  className={`p-4 cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-gradient-to-br from-amber-600 to-amber-500 border-2 border-amber-400 shadow-lg shadow-amber-600/40'
                      : 'bg-[#13151D] border border-amber-600/30 hover:border-amber-500 hover:bg-[#1B1E28]'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isSelected
                          ? 'border-white bg-white'
                          : 'border-amber-600'
                      }`}>
                        {isSelected && (
                          <div className="w-2.5 h-2.5 rounded-full bg-amber-600" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-amber-400'}`} />
                        <p className={`text-sm font-bold ${isSelected ? 'text-white' : 'text-white'}`}>
                          {format(slotDate, 'EEEE d MMMM yyyy', { locale: fr })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className={`w-3 h-3 ${isSelected ? 'text-white/80' : 'text-[#B3B6C2]'}`} />
                        <p className={`text-xs ${isSelected ? 'text-white/80' : 'text-[#B3B6C2]'}`}>
                          {format(slotDate, 'HH:mm', { locale: fr })}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Confirm button */}
          <Button
            className="w-full bg-amber-600 hover:bg-amber-500 text-white font-bold text-sm uppercase transition-all shadow-lg hover:shadow-lg hover:shadow-amber-600/40"
            onClick={() => selectSlotMutation.mutate()}
            disabled={selectedSlotIndex === null || selectSlotMutation.isPending}
          >
            {selectSlotMutation.isPending ? 'Confirmation...' : 'Confirmer ce créneau'}
          </Button>
        </>
      )}
    </div>
  );
}
